// @arc/tokens
//
// Design tokens ported from DesignSystem/DesignSystem.swift in the arc repo.
// Numbers here MUST mirror the Swift source of truth. When a token changes on
// iOS, update both simultaneously.
//
// CLAUDE.md contract (Design System):
//   - No raw Color(), .font(.system(...)), .cornerRadius(N), or .padding(N)
//     literals in view code. The web clients follow the same rule:
//     consume these tokens, do not inline CSS values.
//   - Use `ArcBrandVoice.rangePromise` ("The range remembers.") and
//     `ArcBrandVoice.logoSubtag` ("Hit. See. Fix. Next.") where the iOS
//     surface would; do not re-author brand copy.

// ---------------------------------------------------------------------------
// Spacing from DesignSystem/DesignSystem.swift AppSpacing
// ---------------------------------------------------------------------------

export const spacing = {
  nudge: 1,
  xxs: 2,
  micro: 3,
  xs: 4,
  tight: 5,
  inline: 6,
  sm: 8,
  chip: 10,
  control: 12,
  comfortable: 14,
  md: 16,
  feature: 18,
  lg: 24,
  section: 28,
  xl: 32,
  xxl: 40,
  showcaseInset: 56,
  cardPadding: 20,
  cardInnerPadding: 16,
  cardFeaturePadding: 22,
  minimumInteractiveDimension: 44,
} as const;

export type SpacingToken = keyof typeof spacing;

// ---------------------------------------------------------------------------
// Corner radius from AppCornerRadius
// ---------------------------------------------------------------------------

export const cornerRadius = {
  xsmall: 4,
  tight: 6,
  small: 8,
  compact: 10,
  medium: 12,
  card: 14,
  large: 16,
  xlarge: 20,
  panel: 24,
  xxlarge: 26,
  showcaseStat: 30,
  showcaseCard: 34,
  feature: 28,
  showcase: 44,
  pill: 999,
} as const;

export type CornerRadiusToken = keyof typeof cornerRadius;

// ---------------------------------------------------------------------------
// Border width from AppBorderWidth
// ---------------------------------------------------------------------------

export const borderWidth = {
  hairline: 0.5,
  subtle: 0.75,
  regular: 1.0,
  thick: 1.5,
} as const;

export type BorderWidthToken = keyof typeof borderWidth;

// ---------------------------------------------------------------------------
// Typography roles named semantically, consumed via utility helpers
// ---------------------------------------------------------------------------
//
// The iOS side uses `AppTypography.body()`, `AppTypography.displayHero()`,
// etc. The web mirror exposes size + line-height + weight + letter-spacing so
// the consuming app can bind them to whatever CSS / styled-system layer it
// uses. Do not redeclare sizes inline.
//
// Eyebrows render ALL CAPS with 0.35 tracking; section titles Title Case;
// body sentence case with trailing periods (AGENT_MEMORY Brand Voice guard).

export interface TypographyRole {
  sizePx: number;
  lineHeight: number;
  weight: number;
  letterSpacingEm: number;
  transform?: "uppercase" | "none";
}

export const typography = {
  eyebrow: {
    sizePx: 11,
    lineHeight: 1.2,
    weight: 600,
    letterSpacingEm: 0.35,
    transform: "uppercase",
  },
  caption: {
    sizePx: 12,
    lineHeight: 1.35,
    weight: 500,
    letterSpacingEm: 0.01,
  },
  body: {
    sizePx: 15,
    lineHeight: 1.45,
    weight: 400,
    letterSpacingEm: 0,
  },
  bodyStrong: {
    sizePx: 15,
    lineHeight: 1.45,
    weight: 600,
    letterSpacingEm: 0,
  },
  sectionTitle: {
    sizePx: 18,
    lineHeight: 1.3,
    weight: 600,
    letterSpacingEm: 0,
  },
  headline: {
    sizePx: 22,
    lineHeight: 1.25,
    weight: 700,
    letterSpacingEm: 0,
  },
  displayLarge: {
    sizePx: 36,
    lineHeight: 1.05,
    weight: 800,
    letterSpacingEm: 0,
  },
  displayHero: {
    sizePx: 48,
    lineHeight: 1.05,
    weight: 800,
    letterSpacingEm: 0,
  },
} as const satisfies Record<string, TypographyRole>;

export type TypographyToken = keyof typeof typography;

// ---------------------------------------------------------------------------
// Status / semantic colors
// ---------------------------------------------------------------------------
//
// iOS sources these from `AppThemePalette` which is theme-reactive (light/
// dark). On the web we expose explicit light + dark pairs and let CSS bind
// them to `prefers-color-scheme`.

export type ColorScheme = "light" | "dark";

export interface ThemedColor {
  light: string;
  dark: string;
}

export const palette: Record<string, ThemedColor> = {
  background:      { light: "#F2F7FC", dark: "#080F1A" },
  backgroundRaised:{ light: "rgb(255 255 255 / 0.84)", dark: "rgb(28 41 59 / 0.82)" },
  backgroundHighlight:{ light: "rgb(41 117 227 / 0.16)", dark: "rgb(56 143 250 / 0.24)" },
  surfaceOverlay:  { light: "rgb(255 255 255 / 0.84)", dark: "rgb(28 41 59 / 0.82)" },

  textPrimary:     { light: "#142436", dark: "#F2F7FC" },
  textSecondary:   { light: "#475C75", dark: "#BDC9D9" },
  textMuted:       { light: "#6E829C", dark: "#8A9CB0" },

  glassFill:       { light: "rgb(255 255 255 / 0.84)", dark: "rgb(28 41 59 / 0.82)" },
  glassStroke:     { light: "rgb(117 148 186 / 0.22)", dark: "rgb(84 107 140 / 0.46)" },
  tileFill:        { light: "rgb(250 252 255 / 0.95)", dark: "rgb(26 38 56 / 0.94)" },
  tileStroke:      { light: "rgb(112 145 184 / 0.18)", dark: "rgb(89 115 150 / 0.30)" },
  fieldFill:       { light: "rgb(255 255 255 / 0.98)", dark: "rgb(33 48 69 / 0.96)" },
  fieldStroke:     { light: "rgb(112 145 184 / 0.24)", dark: "rgb(92 117 153 / 0.38)" },
  divider:         { light: "rgb(112 145 184 / 0.18)", dark: "rgb(87 107 138 / 0.28)" },

  buttonPrimaryFill:{ light: "#2975E3", dark: "#388FFA" },
  buttonPrimaryForeground:{ light: "#FFFFFF", dark: "#FFFFFF" },
  buttonSubtleFill:{ light: "#EBF2FC", dark: "rgb(41 56 79 / 0.96)" },
  buttonSubtleForeground:{ light: "#142436", dark: "#F2F7FC" },
  buttonSuccessFill:{ light: "#29A869", dark: "#309E69" },
  buttonSuccessForeground:{ light: "#FFFFFF", dark: "#FFFFFF" },

  accentPrimary:   { light: "#2975E3", dark: "#388FFA" },
  accentSecondary: { light: "#0DA1B8", dark: "#33C7C2" },
  accentCool:      { light: "#1F9EB8", dark: "#63D1F0" },
  accentTertiary:  { light: "#6652C7", dark: "#AD85FA" },
  accentWarm:      { light: "#E56B3E", dark: "#FF8D5E" },

  statusPositive:  { light: "#2EA861", dark: "#4DD17A" },
  statusCaution:   { light: "#DB8A24", dark: "#F5A647" },
  statusNegative:  { light: "#D13D3D", dark: "#F06060" },
  statusHighlight: { light: "#C7AD1A", dark: "#FAD64D" },

  strokeHairline:  { light: "rgb(112 145 184 / 0.12)", dark: "rgb(89 115 150 / 0.18)" },
  strokeRegular:   { light: "rgb(112 145 184 / 0.18)", dark: "rgb(89 115 150 / 0.30)" },
} as const;

export type PaletteToken = keyof typeof palette;

export function color(token: PaletteToken, scheme: ColorScheme = "dark"): string {
  return palette[token][scheme];
}

// ---------------------------------------------------------------------------
// Brand voice from ArcBrandVoice in DesignSystem.swift
// ---------------------------------------------------------------------------

export const brandVoice = {
  rangePromise: "The range remembers.",
  logoSubtag: "Hit. See. Fix. Next.",
} as const;

// ---------------------------------------------------------------------------
// Provenance to color mapping
// ---------------------------------------------------------------------------
//
// Rendering rule: every measurement badge renders the provenance token
// alongside the value. Mapping below is the only place this binding lives.

export const provenancePaletteToken = {
  measured: "statusPositive",
  estimated: "statusCaution",
  proxy: "statusHighlight",
  insufficient: "textMuted",
} as const;

export type ProvenancePaletteToken = keyof typeof provenancePaletteToken;
